import { periodServerMessage } from "../../api/reportPeriod.js";

/** What a server refusal says to the site manager, in plain words. */
export const OUTCOME_TEXT = {
  data_incomplete: "Data is not complete for this period yet.",
  generation_in_progress: "A report for this project is already generating.",
  // Publishing never overwrites: both refusals leave the report Approved.
  already_published: "A file with that name is already on the project site. Nothing was overwritten; the report stays Approved.",
  report_issued: "This report was already issued — reopen it first.",
  invalid_transition: "That action is not allowed from this report's current state.",
  stale_etag: "This report changed since it was read. Reloaded; try again.",
  publish_target_missing: "The project site's Weekly Reports folder is missing.",
  publish_target_not_admitted: "The project site is not set up for publishing.",
  readback_mismatch: "The copy on the project site did not match the approved PDF. Nothing was published; the report stays Approved.",
  pdf_conversion_failed: "The PDF could not be created; nothing was changed. Try again.",
  report_has_unresolved_markers: "The Word file still has review markers in it. Resolve them in Word, then approve again.",
  // SPEC-CIS-COCKPIT-READINESS-ACTIONS-001 photo actions.
  no_assignee: "That inspection has no assigned inspector to remind.",
  inspector_not_on_roster: "The assigned inspector is not on this project's roster.",
  rate_limited: "Today's reminder limit for this project has been reached.",
  mail_unavailable: "The reminder could not be sent; nothing was recorded as sent.",
  external_send_blocked: "The reminder was blocked by the mail policy.",
  not_found: "That inspection was not found.",
  inspection_not_found: "That inspection was not found.",
  already_waived: "That inspection has already been accepted without a photo.",
  fixture_mode: "Not available in the demo data.",
  // SPEC-CIS-REPORT-CUSTOM-PERIOD-001.
  report_ambiguous: "More than one report carries this number. Nothing was changed.",
  period_kind_locked: "A week report and a custom-period report cannot be swapped.",
  period_columns_unprovisioned: "The period columns are not set up on this project yet.",
  // SPEC-CIS-REPORT-PUBLISHED-NUMBER-001: publishing numbers the report; none of these names a number.
  published_number_held: "Another approved report must be published or reopened first.",
  published_number_conflict: "The report number could not be assigned because another report was published at the same time. Nothing was published; the report stays Approved. Try again.",
  published_numbering_migration_pending: "Publishing is paused while this project's earlier reports are being numbered. Approving still works; publish again once that is done.",
  published_number_column_missing: "The project site is not set up for report numbering yet. Nothing was published; the report stays Approved.",
  report_regeneration_required: "Reopen and regenerate this report, then approve and publish.",
  // SPEC-CIS-REPORT-SCHEDULED-PUBLISH-001: scheduling refusals; the report stays Approved.
  schedule_disabled: "Scheduled publishing is switched off. Use Publish now.",
  schedule_timezone_unconfigured: "The site time zone is not set for this project, so the report cannot be scheduled. Use Publish now.",
  already_scheduled: "This report is already scheduled.",
  not_scheduled: "This report is not scheduled.",
  schedule_conflict: "Another report for this project is already scheduled. Hold it first, or use Publish now.",
  changed_since_approval: "The Word file changed after it was approved. Reopen and approve it again, then schedule.",
  approval_record_missing: "This report was approved before scheduling existed. Reopen and approve it again to schedule it, or use Publish now.",
  storage_unavailable: "The server could not record that just now; nothing was changed. Try again.",
  schedule_firing: "The scheduled publication has already started and can no longer be held.",
  publish_in_progress: "This report is being published right now. Wait a moment, then check the list.",
  // Queued generation ended without a draft.
  refused_no_data: "There was no data to report for this period.",
  report_file_locked: "The report file is open or locked in SharePoint. Close it in Word and in the browser, then regenerate again.",
  failed: "The report could not be generated.",
  timeout: "The server did not answer in time. Check the list below before trying again.",
  network_error: "The server could not be reached. Check the list below before trying again.",
};

export const STATUS_TEXT = {
  sent: "Reminder sent.",
  duplicate: "Already reminded today.",
  waived: "Accepted without photo.",
};

export function outcomeText(errorCode, outcome, op) {
  // period_invalid / period_too_long / period_in_future / period_overlap name
  // the rule (and, for an overlap, the conflicting report and its period).
  const period = periodServerMessage(errorCode, outcome && outcome.conflict);
  if (period) return `${period}.`;
  // The holder of the next number is named by its period, never by a number.
  if (errorCode === "published_number_held") {
    const label = outcome && outcome.holder && outcome.holder.periodLabel;
    return label ? `Another approved report (${label}) must be published or reopened first.` : OUTCOME_TEXT.published_number_held;
  }
  if (errorCode === "stale_etag" && op === "approve") return "This report changed a moment ago. Check it and press Approve again.";
  if (errorCode === "report_regeneration_required" && op === "approve") return "Regenerate this draft, then approve.";
  return OUTCOME_TEXT[errorCode] || `Failed (${errorCode || "unknown error"}).`;
}

/** The three readiness lines as the design words them (the count is shown separately). */
export const LINE_LABEL = {
  inspection_results: "Results recorded",
  inspection_photos: "Inspections without photos",
  ncr_decisions: "NCRs awaiting decision",
};

/** The four checks the publish call stands for, in the order the design lists them. */
export const PUBLISH_CHECKS = (code) => [
  "PDF checked against the approved version",
  `Copying to the ${code} project site`,
  "Checking the copy on the project site",
  "Marking the report Published",
];

/**
 * What a finished generation job tells the site manager beyond "the draft is
 * ready" (additive job-status fields; every one is absent on an older server):
 *   - no_prose: the writing service gave nothing usable, the summaries are placeholders
 *   - keptStoredReport: the existing draft was kept
 *   - photosOmitted: the photo cap left some photos out
 *   - proseGrounding: figures in the written summary that no record supports
 * Returns plain-language lines; empty when there is nothing to add.
 */
export function generationNotes(status) {
  if (!status || typeof status !== "object") return [];
  const notes = [];
  if (status.keptStoredReport) {
    notes.push("Your existing draft was kept and nothing was replaced, because the new attempt had no written summary. Generate again to retry.");
  } else if (status.state === "no_prose") {
    notes.push("The written summary could not be produced this time, so the summary sections hold placeholders. Write them in Word, or generate again to retry.");
  }
  const omitted = Number(status.photosOmitted);
  if (omitted > 0) {
    const included = Number(status.photosIncluded);
    notes.push(`${omitted} photo${omitted === 1 ? " was" : "s were"} left out of the report to keep it a workable size${included > 0 ? ` (${included} are included)` : ""}. Every photo stays in the inspection register.`);
  }
  const g = status.proseGrounding;
  if (g && g.ok === false && Array.isArray(g.flags) && g.flags.length) {
    const label = (f) => (f.kind === "ncr_number" ? `NCR ${f.value}` : f.kind === "block" ? `Block ${f.value}` : f.kind === "inspection_count" ? `"${f.text}"` : `date ${f.text}`);
    const shown = g.flags.slice(0, 5).map(label).join(", ");
    const more = (Number(g.flagCount) || g.flags.length) - Math.min(5, g.flags.length);
    notes.push(`Check the written summary: ${shown}${more > 0 ? ` and ${more} more` : ""} could not be matched to the records.`);
  }
  return notes;
}

/**
 * Day redesign phase 2 (SPEC-CIS-COCKPIT-DAY-REDESIGN-P2-001 2.3): the weekly
 * report counts approved records only. `unapprovedRecords` is the count the
 * generation answered with; the sentence is empty when there is none.
 */
export function unapprovedRecordsText(count) {
  const n = Number(count) || 0;
  if (n <= 0) return "";
  return n === 1 ? "1 record in this period is not approved and is left out." : `${n} records in this period are not approved and are left out.`;
}
